import { useState } from 'react';
import { useSiteStatus } from '../hooks';
import { useSubmitUpdates, useJob } from '../../updates/hooks';
import { JobProgress } from '../../updates/JobProgress';
import { buildCachePurgeRequest } from './updatesSelection';
import { QueryState } from '../../../components/QueryState';
import { formatPlatform, healthFlagLabel, isLocalEnvironment } from '../../../lib/format';
import type { SiteStatus } from '../../../lib/types';

const ID = 'em-mgr--site--status';

const TONE_CLASSES: Record<'pass' | 'fail' | 'neutral', string> = {
  pass: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  fail: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  neutral: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
};

function HealthFlag({
  label,
  ok,
  isLocal,
}: {
  label: string;
  ok: boolean | null;
  isLocal: boolean;
}) {
  const { text, tone } = healthFlagLabel(ok, isLocal);
  return (
    <span
      data-id={`${ID}--health-flag`}
      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${TONE_CLASSES[tone]}`}
    >
      {label}: {text}
    </span>
  );
}

// A SiteGround site with SG Optimizer inactive comes back as a FAILED job
// (not a green no-op) — the job result is the proof of what actually flushed.
function CacheActions({ siteId }: { siteId: string }) {
  const submit = useSubmitUpdates();
  const [jobId, setJobId] = useState<string | null>(null);
  const job = useJob(jobId);

  return (
    <div data-id={`${ID}--actions`} className="space-y-2">
      <button
        type="button"
        data-id={`${ID}--purge-cache`}
        disabled={submit.isPending}
        onClick={() =>
          submit.mutate(buildCachePurgeRequest(siteId), {
            onSuccess: (res) => setJobId(res.jobId),
          })
        }
        className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium disabled:opacity-50 dark:border-slate-700"
      >
        {submit.isPending ? 'Purging…' : 'Purge cache'}
      </button>
      {job.data && <JobProgress job={job.data} idBase={`${ID}--cache`} />}
    </div>
  );
}

export function StatusTab({ siteId }: { siteId: string }) {
  const { data, isPending, isError, error } = useSiteStatus(siteId);
  return (
    <QueryState<SiteStatus>
      idBase={ID}
      isPending={isPending}
      isError={isError}
      error={error}
      data={data}
    >
      {(s) => {
        const isLocal = isLocalEnvironment(s.environment, undefined);
        return (
        <div data-id={ID} className="space-y-4">
          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-slate-500">Platform</dt>
              <dd className="font-medium">{formatPlatform(s.platform)}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Core</dt>
              <dd className="font-medium">
                {s.coreVersion}
                {s.coreUpdateVersion && <span className="ml-1 text-blue-600">→ {s.coreUpdateVersion}</span>}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">PHP</dt>
              <dd className="font-medium">{s.phpVersion}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Site time</dt>
              <dd className="font-medium">{s.siteTime}</dd>
            </div>
          </dl>

          <div className="flex flex-wrap gap-2">
            <HealthFlag label="JWT" ok={s.health.jwtOk} isLocal={isLocal} />
            <HealthFlag label="PUC token" ok={s.health.pucTokenOk} isLocal={isLocal} />
            <HealthFlag label="Scanner" ok={s.health.scannerPresent} isLocal={isLocal} />
            {s.health.lastDeploySha && (
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">
                deploy {s.health.lastDeploySha}
              </span>
            )}
          </div>

          <CacheActions siteId={siteId} />

          <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900">
                <tr>
                  <th className="px-3 py-2">Plugin</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Version</th>
                  <th className="px-3 py-2">Update</th>
                </tr>
              </thead>
              <tbody>
                {s.plugins.map((p) => (
                  <tr key={p.slug} data-id={`${ID}--plugin`} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-2 font-medium">{p.name}</td>
                    <td className="px-3 py-2 text-slate-500">{p.status}</td>
                    <td className="px-3 py-2">{p.version}</td>
                    <td className="px-3 py-2">
                      {p.updateVersion ? (
                        <span className="text-blue-600 dark:text-blue-400">{p.updateVersion}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Themes are part of the same status payload, same table shape as plugins. */}
          <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
            <table data-id={`${ID}--themes-table`} className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 dark:bg-slate-900">
                <tr>
                  <th className="px-3 py-2">Theme</th>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">Version</th>
                  <th className="px-3 py-2">Update</th>
                </tr>
              </thead>
              <tbody>
                {s.themes.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-slate-500 dark:text-slate-400">
                      No themes reported.
                    </td>
                  </tr>
                ) : (
                  s.themes.map((t) => (
                    <tr key={t.slug} data-id={`${ID}--theme`} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-3 py-2 font-medium">{t.name}</td>
                      <td className="px-3 py-2 text-slate-500 dark:text-slate-400">
                        {[t.isParent ? 'parent' : 'child', t.active ? 'active' : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </td>
                      <td className="px-3 py-2">{t.version}</td>
                      <td className="px-3 py-2">
                        {t.updateVersion ? (
                          <span className="text-blue-600 dark:text-blue-400">{t.updateVersion}</span>
                        ) : (
                          <span className="text-slate-400 dark:text-slate-500">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        );
      }}
    </QueryState>
  );
}
